import { Typography } from "antd"
import classNames from "classnames"
import React, { useContext, useEffect, useRef, useState } from "react"
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from "recharts"
import ArrowSelectIconDark from "../../Icons/ArrowSelectIconDark"
import AverageTalksTime from "../../Icons/AverageTalksTime"
import AverageTimeBgIcon from "../../Icons/AverageTimeBg"
import CallsTalksTimeBgIcon from "../../Icons/CallsTalksTimeBg"
import CallsTodayBgIcon from "../../Icons/CallsTodayBGIcon"
import CallsTodayIcon from "../../Icons/CallsTodayIcon"
import TalksTimeIcon from "../../Icons/TalksTime"
import SelectBar from "../../Select/Select"
import CallStatsStyle from "./CallStats.style"
import CallWrapperContext from "../../CallWrapper2/CallWrapperContext"
import {
  addMinutes,
  format,
  isSameDay,
  parse,
  subDays,
  subHours,
  subMinutes
} from "date-fns"
import { get } from "../../../services/api"
import { convertDecimalToTime } from "../../../utils/helper"
import CallIcon from "../../Icons/Call"
import TimePickerIcon from "../../Icons/TimePicker"
import OutboundCallIcon from "../../Icons/OutboundCall"
import InBoundCallIcon from "../../Icons/InboundCall"
import Button from "../../Button/Button"
import InfoCircleIcon from "../../Icons/InfoCircle"
import Tooltip from "../../ToolTip/ToolTip.style"

const CallStats = () => {
  const interval = useRef()
  const { credentials } = useContext(CallWrapperContext)

  const [queueStats, setQueueStats] = useState({
    CH: 0,
    ATT: 0,
    IA: 0,
    IAVG: 0,
    IM: 0,
    OANS: 0,
    OAvg: 0,
    OM: 0,
    TT: 0
  })
  const [callsData, setCallsData] = useState([])

  const [callsQueueData, setCallsQueueData] = useState({})
  const [callsFilter, setCallsFilter] = useState("hour")
  const [callsQueueFilter, setCallsQueueFilter] = useState("hour")
  const [callsStatsFilter, setCallsStatsFilter] = useState("today")

  const [callsGraphData, setCallsGraphData] = useState([])
  const [graphTicks, setGraphTicks] = useState([])
  const [callsQueueGraphData, setCallsQueueGraphData] = useState([])

  const fetchStats = () => {
    get(`/api/v1/soft-phone/queue-stats-summary/`).then((res) => {
      console.log("aaaaaaaaaaaaaaaaaa", res.data)
      setCallsQueueData(res.data?.data || {})
    })

    get(`/api/v1/soft-phone/cdr-summary/`, {
      duration: callsFilter == "hour" ? "1 HOUR" : "7 DAY",
      option: callsFilter == "hour" ? "summaryByHour" : "summaryByDay"
    }).then((res) => {
      setCallsData(res.data?.data)
    })
    get(`/api/v1/soft-phone/agent-stats-summary/`).then((res) => {
      setQueueStats(res.data?.data)
    })
  }

  useEffect(() => {
    clearInterval(interval.current)
    fetchStats()
    interval.current = setInterval(fetchStats, 30000)
    return () => {
      clearInterval(interval.current)
    }
  }, [credentials, callsFilter, callsQueueFilter, callsStatsFilter])

  useEffect(() => {
    if (callsFilter == "hour") {
      const now = new Date()
      // Round to nearest lower or upper half hour
      if (now.getMinutes() < 30) now.setMinutes(0, 0, 0)
      else now.setMinutes(30, 0, 0)

      // Generate 12 intervals of 30 minutes (6 hours)
      const intervals = Array.from({ length: 12 }).map((_, i) =>
        subMinutes(now, (11 - i) * 30)
      )

      const data = intervals.map((date) => {
        const key = format(date, "yyyy-MM-dd HH:mm:ss")
        const match = callsData.find((d) => d.dayAndHour === key)
        return {
          time: format(date, "haaa"), // e.g. "10:00PM" or "10:30PM"
          volume: match ? Number(match.volume) : 0
        }
      })

      setCallsGraphData(data)
    } else {
      const days = Array.from({ length: 8 }).map((_, i) =>
        subDays(new Date(), 7 - i)
      )

      // Sum volumes per day
      const chartData = days.map((dayDate) => {
        const totalVolume = callsData
          .filter((d) => {
            const dataDate = parse(
              d.dayAndHour,
              "yyyy-MM-dd HH:mm:ss",
              new Date()
            )
            return isSameDay(dataDate, dayDate)
          })
          .reduce((sum, d) => sum + parseFloat(d.volume), 0)

        return {
          day: format(dayDate, "EEE"), // "Mon", "Tue", etc.
          volume: totalVolume
        }
      })

      setCallsGraphData(chartData)
    }
  }, [callsData])

  useEffect(() => {
    const maxVolume = Math.max(...callsGraphData.map((item) => item.volume))

    // Generate 4 equal values from 0 to maxVolume
    const parts = 4
    const step = maxVolume / parts

    const result = Array.from({ length: parts }, (_, i) => step * (i + 1))
    setGraphTicks(result)
  }, [callsGraphData])

  return (
    <CallStatsStyle>
      <div className="callstats">
        <div className="callstats__queue-stats">
          <div className="head">
            <Typography.Title level={5}>My Stats</Typography.Title>
            <SelectBar
              popupClassName="ant-select-dropdown-bordered"
              style={{ width: 135 }}
              value={callsStatsFilter}
              onChange={(value) => setCallsStatsFilter(value)}
              options={[{ value: "today", label: "Today" }]}
              suffixIcon={<ArrowSelectIconDark />}
            />
          </div>
          <div className="q-warapper">
            <div className="list">
              <div className="list__left">
                <span>
                  <CallIcon />
                </span>
                Queue Calls Today
                <Tooltip
                  title="Number of calls answered for all Queues today."
                  placement="top"
                >
                  <span className="icon">
                    <InfoCircleIcon />
                  </span>
                </Tooltip>
              </div>
              <div className="list__right">{queueStats?.CH}</div>
            </div>
            <div className="list">
              <div className="list__left">
                <span>
                  <TimePickerIcon />
                </span>
                Inbound Average Talk Time
                <Tooltip
                  title="Average length of time spent by an agent on Inbound calls. Includes call center calls."
                  placement="top"
                >
                  <span className="icon">
                    <InfoCircleIcon />
                  </span>
                </Tooltip>
              </div>
              <div className="list__right">
                {convertDecimalToTime(queueStats?.IAVG)}
              </div>
            </div>
            <div className="list">
              <div className="list__left">
                <span>
                  <TimePickerIcon />
                </span>
                Queue Talk Time
                <Tooltip
                  title="Time spent on answered Queue Calls today."
                  placement="top"
                >
                  <span className="icon">
                    <InfoCircleIcon />
                  </span>
                </Tooltip>
              </div>
              <div className="list__right">
                {convertDecimalToTime(queueStats?.TT)}
              </div>
            </div>
            <div className="list">
              <div className="list__left">
                <span>
                  <OutboundCallIcon />
                </span>
                Outbound Calls Today
                <Tooltip
                  title="Number of outbound calls attempted by an agent."
                  placement="top"
                >
                  <span className="icon">
                    <InfoCircleIcon />
                  </span>
                </Tooltip>
              </div>
              <div className="list__right">{queueStats?.OANS}</div>
            </div>
            <div className="list">
              <div className="list__left">
                <span>
                  <TimePickerIcon />
                </span>
                Queue Average Talk Time
                <Tooltip
                  title="Average time spent per call on answered Queue Calls today."
                  placement="top"
                >
                  <span className="icon">
                    <InfoCircleIcon />
                  </span>
                </Tooltip>
              </div>
              <div className="list__right">
                {convertDecimalToTime(queueStats?.ATT)}
              </div>
            </div>
            <div className="list">
              <div className="list__left">
                <span>
                  <TimePickerIcon />
                </span>
                Outbound Talk Time
                <Tooltip
                  title="Time spent by an agent on Outbound calls. Includes talk time and hold time."
                  placement="top"
                >
                  <span className="icon">
                    <InfoCircleIcon />
                  </span>
                </Tooltip>
              </div>
              <div className="list__right">
                {convertDecimalToTime(queueStats?.OM)}
              </div>
            </div>
            <div className="list">
              <div className="list__left">
                <span>
                  <InBoundCallIcon />
                </span>
                Inbound Calls Today
                <Tooltip
                  title="Number of attempted calls inbound to an agent."
                  placement="top"
                >
                  <span className="icon">
                    <InfoCircleIcon />
                  </span>
                </Tooltip>
              </div>
              <div className="list__right">{queueStats?.IA}</div>
            </div>
            <div className="list">
              <div className="list__left">
                <span>
                  <TimePickerIcon />
                </span>
                Outbound Average Talk Time
                <Tooltip
                  title="Average length of time spent on outbound calls by agent answered by remote party. Includes call center calls."
                  placement="top"
                >
                  <span className="icon">
                    <InfoCircleIcon />
                  </span>
                </Tooltip>
              </div>
              <div className="list__right">
                {convertDecimalToTime(queueStats?.OAvg)}
              </div>
            </div>
            <div className="list">
              <div className="list__left">
                <span>
                  <TimePickerIcon />
                </span>
                Inbound Talk Time
                <Tooltip
                  title="Time spent by an agent on Inbound calls. Includes talk time and hold time."
                  placement="top"
                >
                  <span className="icon">
                    <InfoCircleIcon />
                  </span>
                </Tooltip>
              </div>
              <div className="list__right">
                {convertDecimalToTime(queueStats?.IM)}
              </div>
            </div>
          </div>
        </div>
        <div className="call-q-wrapper">
          <div className="q-block">
            <div className="q-block__header">
              <Typography.Title level={5}>Calls</Typography.Title>
              <SelectBar
                popupClassName="ant-select-dropdown-bordered"
                style={{ width: 135 }}
                value={callsFilter}
                onChange={(value) => setCallsFilter(value)}
                options={[
                  { value: "hour", label: "Per Hour" },
                  { value: "day", label: "Per Day" }
                ]}
                suffixIcon={<ArrowSelectIconDark />}
              />
            </div>
            <div className="chart-wrapper">
              <LineChart
                width={500}
                height={300}
                data={callsGraphData}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey={callsFilter == "hour" ? "time" : "day"} />
                <YAxis orientation="right" ticks={graphTicks} />
                <RechartsTooltip />
                <Line
                  type="monotone"
                  dataKey="volume"
                  stroke="#8884d8"
                  strokeWidth={2}
                />
              </LineChart>
            </div>
          </div>

          <div className="q-block">
            <div className="q-block__header">
              <Typography.Title level={5}>All Queues</Typography.Title>
              {/* <Button type="link"> Grid Settings</Button> */}
            </div>
            <div className="list">
              <div className="list__left">
                <span
                  className="square"
                  style={{
                    backgroundColor: `#427b0b`
                  }}
                ></span>
                <span className="text">Calls Offered</span>
                <Tooltip
                  title="Number of calls offered in the selected queue"
                  placement="top"
                >
                  <span className="icon">
                    <InfoCircleIcon />
                  </span>
                </Tooltip>
              </div>
              <div className="list__right">{callsQueueData?.CO}</div>
            </div>
            <div className="list">
              <div className="list__left">
                <span
                  className="square"
                  style={{
                    backgroundColor: `#cdba4f`
                  }}
                ></span>
                <span className="text">Average wait time</span>
                <Tooltip
                  title="Average number of seconds a caller spent in the selected queue before being dispatched to an agent. If none selected, total for all queues will be displayed."
                  placement="top"
                >
                  <span className="icon">
                    <InfoCircleIcon />
                  </span>
                </Tooltip>
              </div>
              <div className="list__right">{callsQueueData?.AWT}</div>
            </div>
            <div className="list">
              <div className="list__left">
                <span
                  className="square"
                  style={{
                    backgroundColor: `#2c658b`
                  }}
                ></span>
                <span className="text">Average handling time</span>
                <Tooltip
                  title="Average time an agent spent on a call. Includes Talk Time (TT), Hold Time (AH), and Disposition Time (ACW)"
                  placement="top"
                >
                  <span className="icon">
                    <InfoCircleIcon />
                  </span>
                </Tooltip>
              </div>
              <div className="list__right">{callsQueueData?.AHT}</div>
            </div>
            <div className="list">
              <div className="list__left">
                <span
                  className="square"
                  style={{
                    backgroundColor: `#f10aca`
                  }}
                ></span>
                <span className="text">Abandon rate</span>
                <Tooltip
                  title="Percentage of calls offered that were abandoned in the selected queue. If none selected, total for all queues will be displayed. (Abandoned Calls) / (Calls Offered)"
                  placement="top"
                >
                  <span className="icon">
                    <InfoCircleIcon />
                  </span>
                </Tooltip>
              </div>
              <div className="list__right">{callsQueueData?.AR}</div>
            </div>
            <div className="list">
              <div className="list__left">
                <span
                  className="square"
                  style={{
                    backgroundColor: `#664cfc`
                  }}
                ></span>
                <span className="text">Calls Handled</span>
                <Tooltip
                  title="Number of calls handled by agents in the selected queue. If none selected, total for all queues will be displayed."
                  placement="top"
                >
                  <span className="icon">
                    <InfoCircleIcon />
                  </span>
                </Tooltip>
              </div>
              <div className="list__right">{callsQueueData?.CH}</div>
            </div>
            <div className="list">
              <div className="list__left">
                <span
                  className="square"
                  style={{
                    backgroundColor: `#b77348`
                  }}
                ></span>
                <span className="text">Calls Volume</span>
                <Tooltip
                  title="Number of calls originating through the selected queue. If none selected, total for all queues will be displayed. Includes answered calls, abandonded calls, forwards, voicemail and assisted calls handled."
                  placement="top"
                >
                  <span className="icon">
                    <InfoCircleIcon />
                  </span>
                </Tooltip>
              </div>
              <div className="list__right">{callsQueueData?.VOL}</div>
            </div>
          </div>
        </div>
      </div>
    </CallStatsStyle>
  )
}

export default CallStats
